import type { PermissionKey } from "@nmt/shared";
import { NavLink, Navigate, Outlet, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../api";
import { cx } from "../../components/ui";
import type { SettingsResponse } from "./useSettings";
import { lastSettings } from "../../components/use-tab";
import { useSession } from "../../session";

/** `needs: null`: everyone's own settings. */
export const SETTINGS_SECTIONS: { to: string; label: string; needs: PermissionKey | null }[] = [
  { to: "company", label: "Company and theme", needs: "settings.manage" },
  { to: "users", label: "Users", needs: "users.manage" },
  { to: "roles", label: "Roles and permissions", needs: "roles.manage" },
  { to: "workflows", label: "Workflows", needs: "settings.manage" },
  { to: "contact-types", label: "Contact types", needs: "settings.manage" },
  { to: "lists", label: "Lists and job numbers", needs: "settings.manage" },
  { to: "work-orders", label: "Work orders, subs, weather", needs: "settings.manage" },
  { to: "calendar", label: "Calendar and reminders", needs: "settings.manage" },
  { to: "email-templates", label: "Email and text templates", needs: "settings.manage" },
  { to: "retail-pricing", label: "Retail pricing", needs: "settings.manage" },
  { to: "materials", label: "Materials", needs: "settings.manage" },
  { to: "automations", label: "Automations", needs: "settings.manage" },
  { to: "ai", label: "AI", needs: "settings.manage" },
  { to: "leads", label: "Website leads", needs: "settings.manage" },
  { to: "sync", label: "Sync", needs: "settings.manage" },
  { to: "quickbooks", label: "QuickBooks", needs: "settings.manage" },
  { to: "comms", label: "Texts and calls", needs: "settings.manage" },
  { to: "import", label: "JobNimbus review", needs: "import.run" },
  { to: "sessions", label: "Signed-in sessions", needs: "users.manage" },
  { to: "activity", label: "Activity log", needs: "audit.view" },
  { to: "recycle-bin", label: "Recycle bin", needs: "records.delete" },
  { to: "profile", label: "My profile", needs: null },
];

export function SettingsLayout() {
  const { can } = useSession();
  const location = useLocation();
  // Once we're done with JobNimbus, its review page goes away.
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => get<SettingsResponse>("/api/settings"), enabled: can("settings.manage") });
  const retired = settings.data?.settings["jobnimbus.retired"] ?? false;
  const sections = SETTINGS_SECTIONS.filter((s) => (!s.needs || can(s.needs)) && !(retired && s.to === "import"));
  const here = location.pathname.replace(/\/$/, "");
  // Settings opens where you were last (on this device), else the first section.
  if (here === "/settings") {
    const last = sections.find((s) => s.to === lastSettings.get()) ?? sections[0];
    return last ? <Navigate to={`/settings/${last.to}`} replace /> : <p className="py-10 text-ink-soft">Your role doesn't include any settings.</p>;
  }
  const open = sections.find((s) => here === `/settings/${s.to}`);
  if (open) lastSettings.set(open.to);
  return (
    <div className="grid gap-6 lg:grid-cols-[200px_minmax(0,1fr)]">
      <nav aria-label="Settings" className="flex gap-1 overflow-x-auto lg:flex-col">
        {sections.map((s) => (
          <NavLink
            key={s.to}
            to={`/settings/${s.to}`}
            className={({ isActive }) =>
              cx("rounded-[var(--radius-control)] px-3 py-2 text-sm font-medium whitespace-nowrap", isActive ? "bg-white text-ink shadow-[var(--shadow-card)]" : "text-ink-soft hover:bg-white/60")
            }
          >
            {s.label}
          </NavLink>
        ))}
      </nav>
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
